import { h, Host, forceUpdate } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations from "../../utils/loadTranslations";
import { createRequestData } from "../../api/publicApi/createCase/createCase";
import { getPublicApiClient } from "../../api/apiClient/publicApiClient";
import { getEnvConfig, setEnv } from "../../api/config";
import { StepBack, StepNavigation, StepNext, FormStep, FormSteps } from "./steps";
import { createApiStore, loadFormDataFromAssetManagementApi, loadFormDataFromPublicApi } from "./stores/apiStore";
import { createAuthStore, SilentAuth, whenAuthenticated } from "../../utils/auth";
import { createFormStore } from "./stores/formStore";
import { ErrorNumberInput } from "./form-elements/error-number-input";
import { ContactInputs } from "./form-elements/contact-inputs";
import { SoftwareInputs } from "./form-elements/software-inputs";
import { ApplicationSelectGroup } from "./form-elements/application-select";
import { MachineSelect } from "./form-elements/machine-select";
import { NotificationTypeSelect } from "./form-elements/notification-type-select";
import { getAssetManagementApiClient } from "../../api/apiClient/assetManagementApiClient";
import { DescriptionInput } from "./form-elements/description-input";
/**
 * @slot contacts-button - Slot for native contacts button to select contact data from phone address book
 * @slot connector - Slot for the connector that handles sending the form data to a backend
 */
export class MfCreateServiceCase {
    constructor() {
        /**
         * If true, the form will be displayed in multiple steps
         */
        this.multipleSteps = false;
        /**
         * Enable file upload in the form
         */
        this.fileUpload = false;
        /**
         * Data that should be prefilled in the form
         * @optional
         */
        this.prefillData = null;
        /**
         * Application name that will be used as origin of the source when sending the form.
         * This can be overridden by the prefillData.origin
         */
        this.origin = "";
        /**
         * The locale to use for translations and the phone input
         */
        this.locale = "en-US";
        /**
         * Environment string to decide which endpoints to use
         */
        this.env = "prod";
        /**
         * use onprem Public API instead of azure
         * @deprecated only used until April 2025
         */
        this.legacyApi = false;
        /**
         * disable silent auth - then the application needs to supply the auth token via @trumpf/ux.utils
         * @optional
         */
        this.disableSilentAuth = false;
        this.tbdbError = null;
        this.apiStore = createApiStore();
        this.authStore = createAuthStore();
        this.formStore = createFormStore();
        this.callId = crypto.randomUUID(); // this browser API only works on localhost and over https
    }
    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.publicApiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.publicApiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                loadFormDataFromPublicApi(this.apiStore, this.publicApiClient);
            }
            else {
                this.tbdbError = event.errorMessage || "Error getting TBDB token";
            }
        });
        this.assetManagementApiClient = getAssetManagementApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuth = whenAuthenticated(() => loadFormDataFromAssetManagementApi(this.apiStore, this.assetManagementApiClient));
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            var _a;
            if (!notificationTypesLoading) {
                this.formStore.state.selectedNotificationType = ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType) || null;
            }
        });
    }
    disconnectedCallback() {
        this.unsubscribeAuth();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    onPrefillDataChange() {
        var _a;
        const notificationType = (_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType;
        if (notificationType) {
            this.formStore.state.selectedNotificationType = notificationType;
        }
    }
    loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }
    onSubmit(event) {
        var _a;
        event.preventDefault();
        const { softwares } = this.apiStore.state;
        const { form, callId, origin, formStore } = this;
        const data = createRequestData({
            form,
            callId,
            softwares,
            origin: ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.origin) || origin,
            selectedMachineTechnology: formStore.state.selectedMachineTechnology
        });
        const files = this.fileUpload
            ? Array.from(form.querySelectorAll("input[type='file']")).reduce((acc, input) => {
                return acc.concat(Array.from(input.files || []));
            }, [])
            : [];
        this.serviceCaseSubmit.emit({ data, callId, files });
        formStore.state.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }
    onCancel() {
        this.serviceCaseCancel.emit();
    }
    render() {
        const t = (key, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const { isAllowedToCreateCase, loading } = this.apiStore.state;
        const { isAuthenticating, authError } = this.authStore.state;
        const { prefillData, multipleSteps } = this;
        const { showErrorNumbers, formIsSubmitted, showMachines } = this.formStore.state;
        const showContent = isAllowedToCreateCase && !(loading || isAuthenticating || authError);
        return (h(Host, { key: 'c51adfbf7f77661540fc3828537a5511abf62a5d' }, !formIsSubmitted && (h("ux-form", { key: '611bda70473c5253a80efb9cba59969a8cd5a3b2', "data-validation-required--msg": t("validation.requiredField") }, h(FormSteps, { key: 'e4999aefe4c30648e23cdfbea781b60a3f8a20a5', multipleSteps: multipleSteps }, h("form", { key: 'db615dfc5db7241cd161f3899a45ef43fd442d5b', onSubmit: (e) => this.onSubmit(e), ref: (el) => (this.form = el) }, h("div", { key: '41fc9a0f0d9cca4044a245c3446d6ffb209ddda5', class: "ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60" }, h("div", { key: '0482118e67293d7097a00c9cb36e0cb152fb3473', class: "ux-text-headline ux-text-headline--giant" }, t("headline.createCase"))), (loading || isAuthenticating) && (h("ux-loader", { key: 'ddb130e10513ed65221c25c6ea11c6fa9e1985aa', class: "ux-u-margin-bottom-40", variant: "spinner" })), authError && (h("div", { key: '3b5b92e1b2aada94fc6231512121bdc04d20229b', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("auth.error"))), isAllowedToCreateCase === false && (h("div", { key: '0d0babe70511df4ec743b7c421dbc521350b24d0', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("missingAuthorization"))), h("div", { key: '974fa0a0e7a66c7c23ac098ff8a78ab83deb747a', style: { display: showContent ? "block" : "none" } }, h(FormStep, { key: 'ebcc8d234dc28dea3315246d0fc875dc327cf263', multipleSteps: multipleSteps }, h(StepNavigation, { key: 'cfea0b6f20142c91fb2bc81e761574f4cc9eeb75', t: t, multipleSteps: multipleSteps, currentStep: 1 }), h("div", { key: '7aced95454e166b413800af61168ce5e0c9c3387', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: '985e1954a04060c0be0789f3446d54117f80dc93', class: "ux-col-12 ux-col-md-10" }, h("div", { key: '3665054ca8a45e4f32328c11f5d4fd1b091361f8', class: "ux-row" }, h("div", { key: '7d9a2be4c3fbf5dc561ef1ef3fafdc82d52822d4', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '44d45dabc06dff145096e77c5c653fccab23b45b', class: "ux-u-margin-bottom-20" }, h(NotificationTypeSelect, { key: '0d2e928b91e921e29f0ddf4342bd70f85e2f6ed5', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showMachines && (h("div", { key: '68bbf98d17a833d530117587e15cab85adee95c1', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '4e477ea600083f35a4ea72074f6584889fe15d72', class: "ux-u-margin-bottom-20" }, h(MachineSelect, { key: '852f94b6cd250a3ca9eb752f24949a04659136c3', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))), h(ApplicationSelectGroup, { key: '8c1ac38f029739990f77d958faf414e45b27f771', t: t, apiStore: this.apiStore, formStore: this.formStore, form: this.form, prefillData: prefillData }), h(SoftwareInputs, { key: '2a0cddd2c81f6c0e49a6cc8fb3d0dcbde80fa46c', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }), showErrorNumbers && (h("div", { key: 'b04cbc4e98ff34595d38e17343953cd35b333ba3', class: "ux-row" }, h("div", { key: '62f75713d513d3103c80833a11c5fdcca3f88eb4', class: "ux-col-12" }, h(ErrorNumberInput, { key: '43308d2b79197cae885e39ea11f006f2b537d42f', t: t, formStore: this.formStore, prefillData: prefillData })))), h("div", { key: '9a6916d3567f5aea2f52e3224edff8c83002ecf1', class: "ux-row" }, h("div", { key: 'f4984825cbd2dfcd249879821c333f749617ea64', class: "ux-col-12" }, h("div", { key: '184719e6975c436ffb4152556d333831b9e4d33f', class: "ux-u-margin-bottom-20" }, h(DescriptionInput, { key: 'd451417f16dba2fbbaa1ed66a13bbefc9234985c', t: t, prefillData: prefillData })))), this.fileUpload && (h("div", { key: 'b6be72995f8b7044f0297b358383bb1c0f77ec45', class: "ux-row" }, h("div", { key: 'a03c1f4acafba0856676870ae725763045d07d50', class: "ux-col-12" }, h("div", { key: 'd6c35d099ef4e869d4dafb61d59870eee7c71280', class: "ux-u-margin-bottom-20" }, h("ux-file-upload", { key: '1ce02f277a24d9073f070633b0326353fa4937c1', "max-file-size": "50", label: t("field.fileUpload.label"), "accepted-files": "image/*,application/*,text/*,video/mp4,", "max-files": "4" }), h("p", { key: '7a77c355aa3826076c4f45b62f6f6f2c39a0a456', class: "ux-text-tiny" }, t("field.fileUpload.info")))))), h(StepNext, { key: '17259fe8196544cb8907267c6a6b5234836d4d99', t: t, multipleSteps: multipleSteps, onCancel: () => this.onCancel() })))), h(FormStep, { key: 'f247dcc554f266685eb39aefe80d1317f539002f', multipleSteps: multipleSteps }, h(StepNavigation, { key: 'e39256ff4f7bec209985c04937b924cbd0b9e8c6', t: t, multipleSteps: multipleSteps, currentStep: 2 }), h("div", { key: '17e5d2c067f1eb7e1278ffbfa62295042c6381df', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: 'c2953c69fb625e4fe919e4a00771a04cc912d96d', class: "fields-wrapper ux-col-12 ux-col-md-10" }, h("div", { key: '71a3a80a93a951530a7b3754a29b52c2c40c2f2d' }, h("slot", { key: '98e141373781bf03594cbda614ad5899abe9073b', name: "contacts-button" })), h(ContactInputs, { key: '29ef4815851409576431d83fbb99b5101877e055', t: t, apiStore: this.apiStore, locale: this.locale }), h("div", { key: 'c7695802c7931aa20493b7952715979a36c32a0f', class: "ux-u-margin-bottom-20" }, h("div", { key: 'c8e34c3de9689ded2d674719a61099b76e76a9ed', class: "ux-text-normal ux-text-normal--light-gray" }, h("p", { key: '174ff77726731eeedbd5298f4491e051c2b25996' }, t("validation.requiredFieldsExplanation")))), h("div", { key: '0898f2cfb4bbc1f58619d468a5b1d796adbac9b2', class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" }, h("ux-button", { key: '36349655ff9a2e2c31e9fece80e573c50d178859', type: "submit", variant: "primary" }, t("button.submit")), h(StepBack, { key: 'b1c88d8e536e25dfba32cd919638e166d9fc7378', t: t, multipleSteps: multipleSteps }), !multipleSteps && (h("ux-button", { key: 'c7683fd24d340946f645626e941f12f5db7a332f', type: "button", onClick: () => this.onCancel(), formnovalidate: true }, t("button.cancel")))))))))))), h("div", { key: '2676a2578478cfaaa41a1f435df6367d9fc604e8', style: { display: formIsSubmitted ? "block" : "none" } }, h("slot", { key: '45f0b84eca438c4f346da7a8faeca37d43419041', name: "connector" })), h(SilentAuth, { key: 'c620970ab3cf1ec26c035c2ef73c46354367776c', config: this.config, disabled: this.disableSilentAuth, authStore: this.authStore })));
    }
    static get is() { return "mf-create-service-case"; }
    static get originalStyleUrls() {
        return {
            "$": ["create-service-case.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["create-service-case.css"]
        };
    }
    static get properties() {
        return {
            "multipleSteps": {
                "type": "boolean",
                "attribute": "multiple-steps",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the form will be displayed in multiple steps"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "fileUpload": {
                "type": "boolean",
                "attribute": "file-upload",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Enable file upload in the form"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "prefillData": {
                "type": "unknown",
                "attribute": "prefill-data",
                "mutable": false,
                "complexType": {
                    "original": "PrefillData | null",
                    "resolved": "PrefillData | null",
                    "references": {
                        "PrefillData": {
                            "location": "import",
                            "path": "../../api/publicApi/createCase/createCase",
                            "id": "src/api/publicApi/createCase/createCase.ts::PrefillData"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "Data that should be prefilled in the form"
                },
                "getter": false,
                "setter": false,
                "defaultValue": "null"
            },
            "origin": {
                "type": "string",
                "attribute": "origin",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Application name that will be used as origin of the source when sending the form.\nThis can be overridden by the prefillData.origin"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"\""
            },
            "locale": {
                "type": "string",
                "attribute": "locale",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The locale to use for translations and the phone input"
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "defaultValue": "\"en-US\""
            },
            "env": {
                "type": "string",
                "attribute": "env",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Environment string to decide which endpoints to use"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"prod\""
            },
            "legacyApi": {
                "type": "boolean",
                "attribute": "legacy-api",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "only used until April 2025"
                        }],
                    "text": "use onprem Public API instead of azure"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "disableSilentAuth": {
                "type": "boolean",
                "attribute": "disable-silent-auth",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "trumpf",
                            "text": "/ux.utils"
                        }, {
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "disable silent auth - then the application needs to supply the auth token via"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "tbdbError": {}
        };
    }
    static get events() {
        return [{
                "method": "serviceCaseSubmit",
                "name": "mfServiceCaseSubmit",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event that is fired when the form is submitted"
                },
                "complexType": {
                    "original": "CreateCaseEventDetail",
                    "resolved": "CreateCaseEventDetail",
                    "references": {
                        "CreateCaseEventDetail": {
                            "location": "local",
                            "path": "/tmp/builds/5MRsrR2d8/0/TRUMPF-corp/trumpf-ux/micro-frontends/service-calls/src/components/create-service-case/create-service-case.tsx",
                            "id": "src/components/create-service-case/create-service-case.tsx::CreateCaseEventDetail"
                        }
                    }
                }
            }, {
                "method": "serviceCaseCancel",
                "name": "mfServiceCaseCancel",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event that is fired when the form is cancelled and the form should be closed"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "locale",
                "methodName": "onLocaleChange"
            }, {
                "propName": "prefillData",
                "methodName": "onPrefillDataChange"
            }];
    }
}
//# sourceMappingURL=create-service-case.js.map
